import { MrbBaseEditViewStore } from "mrb/core";
import { DeviceEditForm } from "application/device-settings/forms";
import { DeviceDowntimeEditViewStore } from "application/device-settings/stores";
import { localizationService } from "common/localization";
import { nakiRadioSystemTypes } from "common/constants";
import { action, computed, makeObservable, observable } from "mobx";
import { isNil } from "lodash";
import { MrbModalParams } from "mrb/common/models";
import { MrbSelectStore } from "mrb/components/select";
import { DropdownItemsResult } from "mrb/components/select/models";

class DeviceEditViewStore extends MrbBaseEditViewStore {
    @observable isFormChanged = false;
    @observable.ref certifiedModes = [];

    @computed get isDowntimeDisabled() {
        return (
            !isNil(this.item) &&
            !isNil(this.item.device) &&
            !isNil(this.item.device.systemType) &&
            this.item.device.systemType.abrv === nakiRadioSystemTypes.mobile
        );
    }

    constructor(rootStore, { routeStore }) {
        const id = rootStore.routerStore.routerState.params.id;
        super(rootStore, {
            id: id,
            name: "device-edit",
            FormClass: DeviceEditForm,
            autoFocusField: "deviceName",
            navigateBack: "master.application.device.list",
            successCreateNotification: null,
            successUpdateNotification: null,
        });
        makeObservable(this);
        this.routeStore = routeStore;

        this.certifiedModeInfoModal = new MrbModalParams(null, null);
        this.deviceDowntimeEditViewStore = new DeviceDowntimeEditViewStore(this.rootStore, this.routeStore, this.form);

        this.modeSelectStore = new MrbSelectStore({
            isSearchable: false,
            virtualized: false,
            initFetch: false,
            isClearable: false,
            textKey: "name",
            valueKey: "id",
            actions: {
                onChange: (option) => {
                    if (option) {
                        this.form.$("certifiedModeId").set(option.value);
                    }
                },
            },
        });
        this.modeSelectStore.originalItems = new DropdownItemsResult([], 1, 0, 0);

        this.registerRouteExitBlocking();
        this.reaction(
            () => this.form.changed || this.deviceDowntimeEditViewStore.disableActionOccured,
            (isChanged) => {
                if (isChanged) {
                    this.isFormChanged = isChanged;
                }
            }
        );

        this.openCertifiedModeInfoModal = this.openCertifiedModeInfoModal.bind(this);
    }

    get(id) {
        return this.routeStore.getUserDevice(id);
    }

    update(resource) {
        resource.prohibitedTime = this.deviceDowntimeEditViewStore.processFormResult(resource.prohibitedTime);
        if (resource.certifiedModeId === "naki-standard") {
            resource.certifiedModeId = null;
            resource.whitelistMode = false;
        } else if (resource.certifiedModeId === "whitelist") {
            resource.certifiedModeId = null;
            resource.whitelistMode = true;
        } else {
            resource.whitelistMode = false;
        }
        this.isFormChanged = false;
        const updates = [this.routeStore.updateUserDevice(resource)];
        if (resource.hideTrending !== !!this.item.hideTrending) {
            updates.push(this.routeStore.updateHideTrending(this.id, resource.hideTrending));
        }
        return Promise.all(updates);
    }

    async onInit() {
        await super.onInit();
        await this.deviceDowntimeEditViewStore.initialize();
        this.deviceDowntimeEditViewStore.buildForm(this.item.prohibitedTime);
        this.initCertifiedModeFormValue();
        this.loadCertifiedModes();
        this.form.$("hideTrending").set(!!this.item.hideTrending);
    }

    onUpdateError(err) {
        this.rootStore.notificationStore.error(
            localizationService.t("APPLICATION.DEVICE_SETTINGS.EDIT.ERROR.GENERIC_MESSAGE")
        );
    }

    registerRouteExitBlocking() {
        this.reaction(
            () => this.isFormChanged,
            (isChanged) => {
                this.routeStore.setIsRouteExitBlocked(isChanged);
            }
        );
    }

    openCertifiedModeInfoModal() {
        this.certifiedModeInfoModal.open();
    }

    @action.bound
    async loadCertifiedModes() {
        try {
            const response = await this.routeStore.findCertifiedModes();
            this.certifiedModes = response.item || response || [];
        } catch (error) {
            this.certifiedModes = [];
        }
        const allOptions = [
            { id: "naki-standard", name: "The Naki Standard" },
            { id: "whitelist", name: "Whitelist Mode" },
            ...this.certifiedModes.map((m) => ({
                id: m.id,
                name: `${m.name}`,
            })),
        ];
        this.modeSelectStore.originalItems = new DropdownItemsResult(
            allOptions,
            1,
            allOptions.length,
            allOptions.length
        );
        const certifiedModeId = this.form.$("certifiedModeId").value;
        const isWhitelistMode = certifiedModeId === "whitelist";
        const actualCertifiedModeId =
            certifiedModeId !== "naki-standard" && certifiedModeId !== "whitelist" ? certifiedModeId : null;
        this._syncModeSelectStore(actualCertifiedModeId, isWhitelistMode);
    }

    _syncModeSelectStore(certifiedModeId, isWhitelistMode) {
        let option;
        if (certifiedModeId) {
            const mode = this.certifiedModes.find((m) => m.id === certifiedModeId);
            option = mode ? { id: mode.id, name: `${mode.name}` } : { id: "naki-standard", name: "The Naki Standard" };
        } else if (isWhitelistMode) {
            option = { id: "whitelist", name: "Whitelist Mode" };
        } else {
            option = { id: "naki-standard", name: "The Naki Standard" };
        }
        this.modeSelectStore.setSelectedItem(option);
    }

    initCertifiedModeFormValue() {
        if (this.item && this.item.certifiedModeId) {
            this.form.$("certifiedModeId").set(this.item.certifiedModeId);
        } else if (this.item && this.item.whitelistMode) {
            this.form.$("certifiedModeId").set("whitelist");
        } else {
            this.form.$("certifiedModeId").set("naki-standard");
        }
    }
}

export default DeviceEditViewStore;
